import { useCallback, useEffect, useState } from 'react';
import { store } from 'redux/store';

import { Document } from 'types';
import documentTypes from 'constants/documentTypes';

import { getPartnerDocuments } from '../api/getPartnerDocuments';
import { getPartnerShareOwnershipCertificate } from '../api/getPartnerShareOwnershipCertificate';

type UseGetPartnerDocumentsProps = {
  partnerId: string;
};

const VALUESHARES = 'VALUESHARES';

export const useGetPartnerDocuments = ({
  partnerId,
}: UseGetPartnerDocumentsProps) => {
  const [documents, setDocuments] = useState<Document[]>([]);

  const [certificateValueSharesDocuments, setCertificateValueSharesDocuments] =
    useState<Document[]>([]);

  const [otherDocuments, setOtherDocuments] = useState<Document[]>([]);

  const [sharesOwnershipDocument, setSharesOwnershipDocument] =
    useState<Document | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);

      if (partnerId) {
        const [documents, certificate] = await Promise.all([
          store.dispatch(getPartnerDocuments({ partnerId })),
          store.dispatch(getPartnerShareOwnershipCertificate({ partnerId })),
        ]);

        const certificateValueSharesDocs = documents.filter(
          (document: Document) =>
            document.category === documentTypes.CERTIFICATES &&
            document.subcategory === VALUESHARES
        );

        const otherDocs = documents.filter(
          (document: Document) =>
            document.category !== documentTypes.CERTIFICATES ||
            document.subcategory !== VALUESHARES
        );

        setCertificateValueSharesDocuments(certificateValueSharesDocs);
        setOtherDocuments(otherDocs);
        setDocuments(documents);
        setSharesOwnershipDocument(certificate);
      } else {
        setDocuments([]);
      }

      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    data: {
      documents,
      certificateValueSharesDocuments,
      otherDocuments,
      sharesOwnershipDocument,
    },
    isLoading,
    isError,
    actions: {
      fetchDocuments,
    },
  };
};
